import { useCallback, useEffect, useState, useRef } from "react";
import { getSwivuAssitText, getUserBoardsList } from "../../network/network.jsx";
import { swivuBoardStore } from "../../bryntum-lib/stores/swivuStores/swivuBoardStore.jsx";
import {
  getFormattedBoardData,
  serverErrorMsg,
  showToast, transformSwivuGridRecords
} from "../../other/utils/utils.jsx";
import {useParams} from "react-router-dom";
import { DynamicParamKey } from "../../other/constants/OtherKeys.jsx";
import { Mask, MessageDialog } from "@bryntum/schedulerpro";
import { toast } from "react-toastify";

/**
 * The board record assigned to selectedBoard has another structure.
 */
const sliceBoard = (board) =>
  board ? { id: board.id, name: board.name, record: board } : null;

const LOCAL_STORAGE_KEY = "swivu:board";
const WINDOW_BOARD_KEY = "swivuBoardSelection";

/**
 * Load the saved board selection.
 * First checks window object, then falls back to localStorage.
 */
export const getSavedBoardSelection = () => {
  try {
    // First, try to get from window object
    if (window[WINDOW_BOARD_KEY]) {
      return window[WINDOW_BOARD_KEY];
    }

    // Fallback to localStorage if not found in window
    const item = localStorage.getItem(LOCAL_STORAGE_KEY);
    return JSON.parse(item);
  } catch (e) {
    console.error(e);
  }
};

/**
 * Save the board selection.
 * Saves to both window object and localStorage.
 */
export const saveBoardSelection = (selectedBoard) => {
  try {
    if (selectedBoard) {
      window[WINDOW_BOARD_KEY] = selectedBoard;
      // Also save to localStorage (fallback for new tabs/page refresh)
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(selectedBoard));
    }
  } catch (e) {
    console.error(e);
  }
};

const getBoardRecordToSelect = (urlParams, boardNameStore, boardLoadFromUrlRef) => {
  // we want to only try to load board from URL on page load
  if (!boardLoadFromUrlRef.current.isBoardLoadAttempted) {
    boardLoadFromUrlRef.current.isBoardLoadAttempted = true;
    const boardSlugInUrl = urlParams?.[DynamicParamKey];
    if (boardSlugInUrl && boardSlugInUrl !== DynamicParamKey) {
      const boardToSelect = boardNameStore.find((b) => b.slug === boardSlugInUrl);
      if (boardToSelect) {
        boardLoadFromUrlRef.current.isBoardLoadSuccess = true;
        return boardToSelect;
      } else showToast({ msg: "Failed to load given board in url", type: "error", duration: "long" });
    }
  }
  const savedBoardSelection = getSavedBoardSelection();
  return savedBoardSelection && boardNameStore.find((b) => b.id === savedBoardSelection.id);
}

/**
 * @description - This hook is responsible for managing boards and enabling the user to select
 *                from among them.
 * @param {boolean} userIsAuthenticated - true if user is authenticated
 * @param {(board) => void} onBoardSelected - a callback to invoke whenever the selected board changes.
 * @param {*} appRolesRef
 */
const useBoards = ({ userIsAuthenticated, onBoardSelected, appRolesRef }) => {
  const urlParams = useParams();
  const boardLoadFromUrlRef = useRef({ isBoardLoadAttempted: false, isBoardLoadSuccess: false });
  const [boardNameStore, setBoardNameStore] = useState(swivuBoardStore());
  const [selectedBoard, setSelectedBoard] = useState(null);

  const reloadSelectedBoard = useCallback(() => {
    if (selectedBoard) onBoardSelected(selectedBoard.record);
  }, [onBoardSelected, selectedBoard]);
  const changeSelectedBoard = useCallback(
    (board) => {
      const slicedBoard = sliceBoard(board);
      setSelectedBoard(slicedBoard);
      onBoardSelected(board);
      saveBoardSelection(
        board ? { id: board.id, name: board.name, slug: board.slug } : null
      );

      if (board) {
        getSwivuAssitText(board.id).then((resp) => {
          transformSwivuGridRecords(resp.data);
        })
      }
    },
    [setSelectedBoard, onBoardSelected]
  );

  const initializeSelectedBoard = useCallback(() => {
    //
    // There is no selected board - either the application is just starting up or the selected
    // board was removed.  Select a new board.  If there are no boards, prompt the user to create one.
    //
    let newBoard = getBoardRecordToSelect(urlParams, boardNameStore, boardLoadFromUrlRef);

    if (!newBoard && boardNameStore.count > 0) {
      newBoard = boardNameStore.first;
    }
    if (!newBoard) {
      MessageDialog.prompt({
        title: 'Name Your Swivu Board',
        okButton: {
          text: "Save Board",
        },
      }).then((result) => {
        if (result.button === MessageDialog.okButton) {
          const boardName = result.text;
          boardNameStore.add({ name: boardName });
          const mask = Mask.mask("Adding board...");
          toast.success(`Board added successfully!`);
          mask.destroy();
        }
      });
    }
    changeSelectedBoard(newBoard);
  }, [boardNameStore, changeSelectedBoard]); // TODO: fix, not adding urlParams in deps since it cause page to re-reload

  const clearSelectedBoard = useCallback(() => {
    changeSelectedBoard(null);
  }, [changeSelectedBoard]);

  const clearBoards = useCallback(() => {
    clearSelectedBoard();
    boardNameStore?.destroy();
    setBoardNameStore(swivuBoardStore());
  }, [clearSelectedBoard, boardNameStore, setBoardNameStore]);

  const getRefreshUserBoardsListing = useCallback(() => {
    return getUserBoardsList().then((boardResp) => {
      boardNameStore.data = boardResp.data.map((boardData) => getFormattedBoardData(boardData));
    }).catch(() => {
      showToast({msg: serverErrorMsg(), type: "error"});
    });
  }, [boardNameStore]);

  useEffect(() => {
    if (userIsAuthenticated) {

      getRefreshUserBoardsListing().then(() => {
        boardNameStore.appRoles = appRolesRef.current;
        initializeSelectedBoard();
      });

      // after every 3 minutes load user boards data
      const interval = setInterval(() => {
        getRefreshUserBoardsListing();
      }, 180000);

      return () => {
        clearInterval(interval)
      }
    }
  }, [userIsAuthenticated, boardNameStore, initializeSelectedBoard, getRefreshUserBoardsListing]);

  useEffect(() => {
    if (userIsAuthenticated) {
      // Respond to changes in the board store.
      return boardNameStore.on({
        idChange({ record: _record }) {
          // The ID of a record changes only when the record is first saved.  If there is no selected
          // board, then the saved record is the one created through boardFormPopup.  Select the new
          // board automatically.
          if (!selectedBoard) {
            initializeSelectedBoard();
          }
        },
        update(boardToUpdate) {
          // Let changes to the selected board name be reflected in the board name field in the header.
          if (
            boardToUpdate.changes?.name &&
            selectedBoard?.id === boardToUpdate.record.id
          ) {
            setTimeout(() =>
              setSelectedBoard(sliceBoard(boardToUpdate.record))
            );
          }
        },
        refresh({ action }) {
          /** When board data is refreshed (e.g., from periodic updates), update the selected board instance
           *  So this is because we add a polling on userBoards every 3 minutes.
           *  The selectedBoard reference is not updated.
           */
          if (action === 'dataset' && selectedBoard) {
            const updatedBoardRecord = boardNameStore.getById(selectedBoard.id);
            if (updatedBoardRecord) {
              setSelectedBoard((prevRecord) => {
                const boardRecord = sliceBoard(updatedBoardRecord);
                if (boardRecord) {
                  prevRecord.record = boardRecord.record;
                  prevRecord.name = updatedBoardRecord.name;
                }
                return prevRecord;
              });
            }
          }
        },
        remove({ records }) {
          if (selectedBoard.id === records[0].id) {
            initializeSelectedBoard();
          }
        }
      });
    }
  }, [
    userIsAuthenticated,
    boardNameStore,
    selectedBoard,
    initializeSelectedBoard
  ]);

  return {
    boardNameStore, // This store contains the list of boards available to the current user.
    selectedBoard, // The selected board - only the root properties.  May be null.  If not null,
    //   must match an element of boardNameStore
    changeSelectedBoard, // Function: select a new board.
    clearSelectedBoard, // Function: clear the current board selection.
    clearBoards, // Function clear the current board selection and the entire board name store.
    reloadSelectedBoard,
    boardLoadFromUrlRef,
    getRefreshUserBoardsListing,
  };
};

export default useBoards;
